import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import maskSensitiveInfo from '@/integrations/supabase/utils';
import type { MoveHistoryItem } from '@/lib/game';

export function useMoveHistory(gameId?: string, onGameUpdated?: () => void) {
  const [moveHistory, setMoveHistory] = useState<MoveHistoryItem[]>([]);

  const loadMoveHistory = useCallback(async () => {
    if (!gameId) {
      setMoveHistory([]);
      return;
    }

    const { data, error } = await supabase
      .from('moves')
      .select('*, games!inner(*)')
      .eq('game_id', gameId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Erro ao carregar histórico:', maskSensitiveInfo(error));
      return;
    }

    const history = (data || []).map((move) => ({
      player: move.current_player,
      from: move.from_position,
      to: move.to_position,
      type: move.is_capture ? 'captura' : move.is_promotion ? 'promoção' : 'movimento',
    }));

    setMoveHistory(history);
  }, [gameId]);

  useEffect(() => {
    if (!gameId) {
      setMoveHistory([]);
      return;
    }

    loadMoveHistory();

    const channel = supabase
      .channel(`game:${gameId}:history`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'games',
          filter: `id=eq.${gameId}`,
        },
        (payload) => {
          console.log('Game update received:', payload);
          onGameUpdated?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'moves',
          filter: `game_id=eq.${gameId}`,
        },
        (payload) => {
          console.log('Move update received:', payload);
          loadMoveHistory();
        }
      )
      .subscribe((status) => {
        console.log('History channel status:', status);
      });

    return () => {
      channel.unsubscribe();
    };
  }, [gameId, loadMoveHistory, onGameUpdated]);

  const resetMoveHistory = useCallback(() => {
    setMoveHistory([]);
  }, []);

  return {
    moveHistory,
    resetMoveHistory,
    refetchMoveHistory: loadMoveHistory,
  };
}

